import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider,
  Alert,
  Skeleton,
  Paper,
  IconButton,
  Tooltip,
  Badge,
  LinearProgress,
  Button
} from '@mui/material';
import {
  TrendingUp,
  People,
  Description,
  CheckCircle,
  Warning,
  Error,
  Schedule,
  Business,
  Assessment,
  Notifications,
  Refresh,
  Security,
  CloudSync,
  AutoAwesome,
  Visibility,
  Download,
  Settings
} from '@mui/icons-material';
import PageHeader from './ui/PageHeader.tsx';
import StatCard from './ui/StatCard.tsx';
import ActionButton from './ui/ActionButton.tsx';
import { dashboardApi, DashboardStats } from '../services/dashboardApi.ts';

interface UnifiedDashboardProps {
  user?: any;
}

const UnifiedDashboard: React.FC<UnifiedDashboardProps> = ({ user }) => {
  const [data, setData] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  // Log para debugging de la URL del backend
  console.log('🔗 UnifiedDashboard - BACKEND_URL:', process.env.REACT_APP_BACKEND_URL);
  console.log('🔗 UnifiedDashboard - Todas las variables de entorno:', {
    REACT_APP_API_URL: process.env.REACT_APP_API_URL,
    REACT_APP_BACKEND_URL: process.env.REACT_APP_BACKEND_URL,
    NODE_ENV: process.env.NODE_ENV
  });

  // Función para obtener la URL del backend con validación
  const getBackendUrl = () => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'https://sign-bk-qa.smartdc.ar';
    console.log('🔗 UnifiedDashboard - getBackendUrl():', backendUrl);
    return backendUrl;
  };

  useEffect(() => {
    loadDashboardData();
    const interval = setInterval(loadDashboardData, 30000); // Actualizar cada 30 segundos
    return () => clearInterval(interval);
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      const dashboardData = await dashboardApi.getUnifiedDashboard();
      setData(dashboardData);
    } catch (err: any) {
      setError(err.message || 'Error al cargar datos del dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadDashboardData();
    setRefreshing(false);
  };

  const getHealthColor = (status: string) => {
    switch (status) {
      case 'HEALTHY': return 'success';
      case 'WARNING': return 'warning';
      case 'CRITICAL': return 'error';
      default: return 'default';
    }
  };

  const getSourceIcon = (source: string) => {
    switch (source) {
      case 'SYSTEM': return <Business />;
      case 'ADOBE_SIGN': return <AutoAwesome />;
      default: return <Description />;
    }
  };

  const getSourceColor = (source: string) => {
    switch (source) {
      case 'SYSTEM': return 'primary';
      case 'ADOBE_SIGN': return 'secondary';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <PageHeader 
          title="Dashboard Unificado" 
          subtitle="Vista completa del sistema y Adobe Sign"
        />
        <Grid container spacing={3}>
          {[...Array(8)].map((_, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Skeleton variant="rectangular" height={120} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button onClick={loadDashboardData} variant="contained">
          Reintentar
        </Button>
      </Box>
    );
  }

  if (!data) return null;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header con botón de refresh */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <PageHeader 
          title="Dashboard Unificado" 
          subtitle="Vista completa del sistema y Adobe Sign"
        />
        <Tooltip title="Actualizar datos">
          <IconButton 
            onClick={handleRefresh} 
            disabled={refreshing}
            color="primary"
          >
            <Refresh sx={{ transform: refreshing ? 'rotate(360deg)' : 'none', transition: 'transform 0.5s' }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Notificaciones importantes */}
      {data.notifications.length > 0 && (
        <Box sx={{ mb: 3 }}>
          {data.notifications.map((notification) => (
            <Alert 
              key={notification.id} 
              severity={notification.type} 
              sx={{ mb: 1 }}
              action={
                <Chip 
                  label={notification.source} 
                  size="small" 
                  color={getSourceColor(notification.source)}
                />
              }
            >
              <Typography variant="subtitle2">{notification.title}</Typography>
              <Typography variant="body2">{notification.message}</Typography>
            </Alert>
          ))}
        </Box>
      )}

      {/* Estadísticas principales */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Sistema */}
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Documentos Totales"
            value={data.system.totalDocuments}
            icon={<Description />}
            color="primary"
            trend="+12%"
            trendDirection="up"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Verificaciones Pendientes"
            value={data.system.pendingVerifications}
            icon={<Schedule />}
            color={data.system.pendingVerifications > 10 ? "warning" : "primary"}
            trend={data.system.pendingVerifications > 10 ? "¡Requiere atención!" : "Normal"}
            trendDirection={data.system.pendingVerifications > 10 ? "up" : "down"}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Firmas Completadas"
            value={data.system.completedSignatures}
            icon={<CheckCircle />}
            color="success"
            trend="+8%"
            trendDirection="up"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Clientes Registrados"
            value={data.system.totalClients}
            icon={<People />}
            color="info"
            trend="+5%"
            trendDirection="up"
          />
        </Grid>
      </Grid>

      {/* Adobe Sign Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12}>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
            <AutoAwesome sx={{ mr: 1, color: 'secondary.main' }} />
            Adobe Sign
            <Chip 
              label={data.authorization.authorized ? 'Conectado' : 'Desconectado'} 
              color={data.authorization.authorized ? 'success' : 'error'} 
              size="small" 
              sx={{ ml: 2 }}
            />
          </Typography>
        </Grid>
        
        {/* Botones de acción de Adobe Sign */}
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={handleRefresh}
              disabled={refreshing}
            >
              Verificar
            </Button>
            
            {!data.authorization.authorized ? (
              <Button
                variant="contained"
                color="primary"
                startIcon={<Security />}
                onClick={() => window.open(`${getBackendUrl()}/adobe-sign/auth/start`, '_blank')}
              >
                Conectar
              </Button>
            ) : (
              <>
                <Button
                  variant="outlined"
                  color="warning"
                  startIcon={<CloudSync />}
                  onClick={() => window.open(`${getBackendUrl()}/adobe-sign/refresh-token`, '_blank')}
                >
                  Refrescar Token
                </Button>
                <Button
                  variant="outlined"
                  color="info"
                  startIcon={<Security />}
                  onClick={() => window.open(`${getBackendUrl()}/adobe-sign/auth/start`, '_blank')}
                >
                  Reautorizar
                </Button>
              </>
            )}
          </Box>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Acuerdos Totales"
            value={data.adobeSign.totalAgreements}
            icon={<Business />}
            color="secondary"
            trend="Adobe Sign"
            trendDirection="up"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Acuerdos Firmados"
            value={data.adobeSign.signedAgreements}
            icon={<CheckCircle />}
            color="success"
            trend="+15%"
            trendDirection="up"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Acuerdos Pendientes"
            value={data.adobeSign.pendingAgreements}
            icon={<Schedule />}
            color="warning"
            trend="En proceso"
            trendDirection="up"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Webhook Status"
            value={data.webhook.status}
            icon={<CloudSync />}
            color={data.webhook.status === 'ACTIVE' ? 'success' : 'error'}
            trend={`${data.webhook.eventCount} eventos`}
            trendDirection="up"
          />
        </Grid>
      </Grid>

      {/* Estado del sistema */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                <Assessment sx={{ mr: 1 }} />
                Estado del Sistema
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Sistema Principal</Typography>
                  <Chip 
                    label={data.system.systemHealth} 
                    color={getHealthColor(data.system.systemHealth)} 
                    size="small"
                  />
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={data.system.systemHealth === 'HEALTHY' ? 100 : data.system.systemHealth === 'WARNING' ? 60 : 20}
                  color={getHealthColor(data.system.systemHealth)}
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Adobe Sign</Typography>
                  <Chip 
                    label={data.adobeSign.systemHealth} 
                    color={getHealthColor(data.adobeSign.systemHealth)} 
                    size="small"
                  />
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={data.adobeSign.systemHealth === 'HEALTHY' ? 100 : data.adobeSign.systemHealth === 'WARNING' ? 60 : 20}
                  color={getHealthColor(data.adobeSign.systemHealth)}
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>
              {data.authorization.needsReauthorization && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  Adobe Sign requiere nueva autorización
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                <Notifications sx={{ mr: 1 }} />
                Actividad Reciente
              </Typography>
              <List sx={{ maxHeight: 300, overflow: 'auto' }}>
                {data.recentActivity.map((activity, index) => (
                  <React.Fragment key={activity.id}>
                    <ListItem alignItems="flex-start">
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: getSourceColor(activity.source) }}>
                          {getSourceIcon(activity.source)}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={activity.title}
                        secondary={
                          <React.Fragment>
                            <Typography component="span" variant="body2" color="text.primary">
                              {activity.description}
                            </Typography>
                            <br />
                            <Typography component="span" variant="caption" color="text.secondary">
                              {new Date(activity.timestamp).toLocaleString()}
                            </Typography>
                            <Chip 
                              label={activity.source} 
                              size="small" 
                              color={getSourceColor(activity.source)}
                              sx={{ ml: 1 }}
                            />
                          </React.Fragment>
                        }
                      />
                      <Chip 
                        label={activity.status} 
                        size="small" 
                        color={activity.status === 'completed' ? 'success' : 'default'}
                      />
                    </ListItem>
                    {index < data.recentActivity.length - 1 && <Divider variant="inset" component="li" />}
                  </React.Fragment>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Acciones rápidas */}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Acciones Rápidas
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <ActionButton
            title="Verificar Identidad"
            description="Revisar verificaciones pendientes"
            icon={<Visibility />}
            color="primary"
            onClick={() => window.location.href = '/verificaciones-pendientes'}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <ActionButton
            title="Gestionar Documentos"
            description="Ver y administrar documentos"
            icon={<Description />}
            color="secondary"
            onClick={() => window.location.href = '/documentos'}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <ActionButton
            title="Clientes"
            description="Administrar clientes"
            icon={<People />}
            color="info"
            onClick={() => window.location.href = '/clientes'}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <ActionButton
            title="Configuración"
            description="Configurar Adobe Sign"
            icon={<Settings />}
            color="warning"
            onClick={() => window.location.href = '/adobe-sign'}
          />
        </Grid>
      </Grid>

      {/* Información de actualización */}
      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Typography variant="caption" color="text.secondary">
          Última actualización: {new Date(data.lastUpdated).toLocaleString()}
        </Typography>
      </Box>
    </Box>
  );
};

export default UnifiedDashboard;
